export function PropertyConditionImpact() {
  return (
    <section className="py-20 px-4 bg-gray-50">
      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-16">
          <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-6">
            How Property Condition Impacts Value
          </h2>
          <p className="text-xl text-gray-600 max-w-4xl mx-auto leading-relaxed">
            Property condition is one of the most significant factors affecting apartment building value. Understanding how condition impacts valuation helps you set realistic expectations and make informed decisions about repairs and improvements.
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-12 items-center mb-16">
          <div>
            <h3 className="text-3xl font-bold text-gray-900 mb-6">The Condition-Value Relationship</h3>
            <p className="text-lg text-gray-700 mb-4 leading-relaxed">
              Property condition affects value in multiple ways: it impacts rental rates, operating expenses, buyer pool, financing availability, and perceived risk. A well-maintained property commands premium pricing, while a property needing significant repairs sells at a substantial discount.
            </p>
            <p className="text-lg text-gray-700 mb-6 leading-relaxed">
              The difference between excellent condition and poor condition can easily represent 30-50% of a property's value. This makes honest condition assessment critical for accurate valuation and successful transactions.
            </p>
            <div className="bg-teal-50 border-l-4 border-teal-600 p-6 rounded-r-lg">
              <h4 className="font-bold text-gray-900 mb-2 text-lg">Value Impact Range:</h4>
              <p className="text-gray-800 text-xl font-semibold mb-2">30-50% difference</p>
              <p className="text-gray-600">Between excellent and poor condition properties</p>
            </div>
          </div>
          <div>
            <img
              src="https://public.readdy.ai/ai/img_res/41480e78ebbecc65c68103f32f303028.jpg"
              alt="Property condition impact on apartment building value"
              className="w-full h-96 object-cover object-top rounded-lg shadow-lg"
            />
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-lg p-8 lg:p-12 mb-12">
          <h3 className="text-3xl font-bold text-gray-900 mb-8 text-center">Property Condition Classifications</h3>
          
          <div className="space-y-6">
            <div className="bg-gradient-to-r from-green-50 to-green-100 rounded-lg p-6 border-l-4 border-green-600">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 flex items-center justify-center bg-green-600 rounded-full flex-shrink-0">
                  <i className="ri-star-fill text-white text-xl"></i>
                </div>
                <div className="flex-1">
                  <h4 className="text-2xl font-bold text-gray-900 mb-3">Excellent Condition (Class A)</h4>
                  <p className="text-gray-700 mb-3 leading-relaxed">
                    Recently renovated or new construction within the last 5-10 years. Modern finishes, updated systems, premium amenities, and minimal deferred maintenance.
                  </p>
                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <p className="font-semibold text-gray-900 mb-2">Characteristics:</p>
                      <ul className="space-y-1 text-gray-700">
                        <li>• New or recently replaced roof, HVAC, plumbing</li>
                        <li>• Modern kitchens and bathrooms</li>
                        <li>• Fresh paint and flooring throughout</li>
                        <li>• Updated electrical and mechanical systems</li>
                      </ul>
                    </div>
                    <div>
                      <p className="font-semibold text-gray-900 mb-2">Value Impact:</p>
                      <ul className="space-y-1 text-gray-700">
                        <li>• Commands 20-30% premium over average</li>
                        <li>• Attracts highest quality tenants</li>
                        <li>• Lower cap rates (4-6%)</li>
                        <li>• Easiest to finance and sell</li>
                      </ul>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-gradient-to-r from-blue-50 to-blue-100 rounded-lg p-6 border-l-4 border-blue-600">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 flex items-center justify-center bg-blue-600 rounded-full flex-shrink-0">
                  <i className="ri-thumb-up-fill text-white text-xl"></i>
                </div>
                <div className="flex-1">
                  <h4 className="text-2xl font-bold text-gray-900 mb-3">Good Condition (Class B+)</h4>
                  <p className="text-gray-700 mb-3 leading-relaxed">
                    Well-maintained property with some updates. Systems are functional with remaining useful life. Minor cosmetic updates may be needed but no major repairs required.
                  </p>
                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <p className="font-semibold text-gray-900 mb-2">Characteristics:</p>
                      <ul className="space-y-1 text-gray-700">
                        <li>• Roof and major systems have 5-10 years life</li>
                        <li>• Clean, functional units</li>
                        <li>• Regular maintenance performed</li>
                        <li>• Some dated finishes but acceptable</li>
                      </ul>
                    </div>
                    <div>
                      <p className="font-semibold text-gray-900 mb-2">Value Impact:</p>
                      <ul className="space-y-1 text-gray-700">
                        <li>• Commands 10-15% premium over average</li>
                        <li>• Attracts quality tenants</li>
                        <li>• Moderate cap rates (6-7%)</li>
                        <li>• Good financing options available</li>
                      </ul>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-gradient-to-r from-yellow-50 to-yellow-100 rounded-lg p-6 border-l-4 border-yellow-600">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 flex items-center justify-center bg-yellow-600 rounded-full flex-shrink-0">
                  <i className="ri-checkbox-circle-fill text-white text-xl"></i>
                </div>
                <div className="flex-1">
                  <h4 className="text-2xl font-bold text-gray-900 mb-3">Average Condition (Class B)</h4>
                  <p className="text-gray-700 mb-3 leading-relaxed">
                    Functional property showing normal wear and tear. Some deferred maintenance. Will need updates and repairs within 1-3 years but currently operational.
                  </p>
                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <p className="font-semibold text-gray-900 mb-2">Characteristics:</p>
                      <ul className="space-y-1 text-gray-700">
                        <li>• Systems functional but aging</li>
                        <li>• Visible wear and tear</li>
                        <li>• Some deferred maintenance</li>
                        <li>• Dated but functional finishes</li>
                      </ul>
                    </div>
                    <div>
                      <p className="font-semibold text-gray-900 mb-2">Value Impact:</p>
                      <ul className="space-y-1 text-gray-700">
                        <li>• Market baseline pricing</li>
                        <li>• Attracts average tenants</li>
                        <li>• Moderate cap rates (7-8%)</li>
                        <li>• Standard financing available</li>
                      </ul>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-gradient-to-r from-orange-50 to-orange-100 rounded-lg p-6 border-l-4 border-orange-600">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 flex items-center justify-center bg-orange-600 rounded-full flex-shrink-0">
                  <i className="ri-tools-fill text-white text-xl"></i>
                </div>
                <div className="flex-1">
                  <h4 className="text-2xl font-bold text-gray-900 mb-3">Fair Condition (Class C)</h4>
                  <p className="text-gray-700 mb-3 leading-relaxed">
                    Property needs significant repairs and updates. Deferred maintenance is evident. Major systems may be near end of useful life. Requires substantial investment to bring to market standards.
                  </p>
                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <p className="font-semibold text-gray-900 mb-2">Characteristics:</p>
                      <ul className="space-y-1 text-gray-700">
                        <li>• Major systems need replacement soon</li>
                        <li>• Significant deferred maintenance</li>
                        <li>• Outdated units and common areas</li>
                        <li>• Visible deterioration</li>
                      </ul>
                    </div>
                    <div>
                      <p className="font-semibold text-gray-900 mb-2">Value Impact:</p>
                      <ul className="space-y-1 text-gray-700">
                        <li>• 15-25% discount from market</li>
                        <li>• Limited tenant pool</li>
                        <li>• Higher cap rates (8-9%)</li>
                        <li>• Financing may be challenging</li>
                      </ul>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-gradient-to-r from-red-50 to-red-100 rounded-lg p-6 border-l-4 border-red-600">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 flex items-center justify-center bg-red-600 rounded-full flex-shrink-0">
                  <i className="ri-alert-fill text-white text-xl"></i>
                </div>
                <div className="flex-1">
                  <h4 className="text-2xl font-bold text-gray-900 mb-3">Poor Condition (Class D)</h4>
                  <p className="text-gray-700 mb-3 leading-relaxed">
                    Property requires extensive repairs or renovation. Major systems failing or failed. Significant safety or code violations may exist. Substantial capital investment required.
                  </p>
                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <p className="font-semibold text-gray-900 mb-2">Characteristics:</p>
                      <ul className="space-y-1 text-gray-700">
                        <li>• Major systems failed or failing</li>
                        <li>• Extensive deferred maintenance</li>
                        <li>• Possible code violations</li>
                        <li>• May have structural issues</li>
                      </ul>
                    </div>
                    <div>
                      <p className="font-semibold text-gray-900 mb-2">Value Impact:</p>
                      <ul className="space-y-1 text-gray-700">
                        <li>• 30-50% discount from market</li>
                        <li>• Very limited tenant appeal</li>
                        <li>• High cap rates (9-10%+)</li>
                        <li>• Cash buyers or hard money only</li>
                      </ul>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="grid lg:grid-cols-2 gap-12 items-center">
          <div>
            <img
              src="https://readdy.ai/api/search-image?query=apartment-building-major-systems-roof-hvac-plumbing-electrical-mechanical-systems-building-infrastructure-property-maintenance-system-replacement-capital-improvements-building-components-inspection&width=800&height=600&seq=major-systems-001&orientation=landscape"
              alt="Major building systems affecting property value"
              className="w-full h-96 object-cover object-top rounded-lg shadow-lg"
            />
          </div>
          <div>
            <h3 className="text-3xl font-bold text-gray-900 mb-6">Key Systems That Impact Value</h3>
            <p className="text-lg text-gray-700 mb-6 leading-relaxed">
              Certain building systems have outsized impact on property value because of their high replacement costs and critical importance to operations. Here are the most important systems buyers evaluate:
            </p>
            <div className="space-y-4">
              <div className="bg-white rounded-lg p-5 shadow-md">
                <h4 className="font-bold text-gray-900 mb-2 flex items-center gap-2">
                  <i className="ri-home-4-line text-teal-600"></i>
                  Roof System
                </h4>
                <p className="text-gray-600">Replacement cost: $50K-$200K+ | Remaining life is critical to value</p>
              </div>
              <div className="bg-white rounded-lg p-5 shadow-md">
                <h4 className="font-bold text-gray-900 mb-2 flex items-center gap-2">
                  <i className="ri-temp-hot-line text-teal-600"></i>
                  HVAC Systems
                </h4>
                <p className="text-gray-600">Replacement cost: $30K-$150K+ | Age and efficiency affect operating costs</p>
              </div>
              <div className="bg-white rounded-lg p-5 shadow-md">
                <h4 className="font-bold text-gray-900 mb-2 flex items-center gap-2">
                  <i className="ri-water-flash-line text-teal-600"></i>
                  Plumbing System
                </h4>
                <p className="text-gray-600">Replacement cost: $40K-$200K+ | Leaks and pipe condition are major concerns</p>
              </div>
              <div className="bg-white rounded-lg p-5 shadow-md">
                <h4 className="font-bold text-gray-900 mb-2 flex items-center gap-2">
                  <i className="ri-flashlight-line text-teal-600"></i>
                  Electrical System
                </h4>
                <p className="text-gray-600">Upgrade cost: $30K-$100K+ | Capacity and code compliance matter</p>
              </div>
              <div className="bg-white rounded-lg p-5 shadow-md">
                <h4 className="font-bold text-gray-900 mb-2 flex items-center gap-2">
                  <i className="ri-building-line text-teal-600"></i>
                  Foundation & Structure
                </h4>
                <p className="text-gray-600">Repair cost: $50K-$500K+ | Structural issues severely impact value</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}